import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Check, X, Sparkles, Crown, Zap, ArrowRight, Star, Tag, Loader2, Clock, Info } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import Logo from '../components/Logo';
import axios from 'axios';
import api from '../api';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const Pricing = () => {
  // Detect Google Play TWA context — Razorpay is not permitted inside the Android app
  const isTWA = document.referrer.includes('android-app://in.matchitup.twa');
  const navigate = useNavigate();
  const [isAnnual, setIsAnnual] = useState(false);
  const [showCheckoutModal, setShowCheckoutModal] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [couponCode, setCouponCode] = useState('');
  const [couponValidating, setCouponValidating] = useState(false);
  const [appliedCoupon, setAppliedCoupon] = useState(null);
  const [trialEnabled, setTrialEnabled] = useState(null); // null = loading, true/false = known
  const [subscriptionLoading, setSubscriptionLoading] = useState(false);
  const [paymentsEnabled, setPaymentsEnabled] = useState(false);
  const [userSubscription, setUserSubscription] = useState(null);

  // Determine trial badge visibility based on user's current subscription status.
  // Trial is always available for new signups (no external platform toggle needed).
  useEffect(() => {
    let isMounted = true;
    const fetchStatus = async () => {
      const token = localStorage.getItem('token');
      if (token) {
        try {
          const subResponse = await api.get(`${API}/subscription/status`);
          if (!isMounted) return;
          setUserSubscription(subResponse.data);
          const tier = (subResponse.data.tier || '').toLowerCase();
          // Hide trial badge if user is already on Pro/Elite, or has already used their trial
          const alreadyOnPaidTier = tier === 'pro' || tier === 'elite' || subResponse.data.pro_trial_used === true;
          setTrialEnabled(!alreadyOnPaidTier);
        } catch {
          // If status check fails, still show the trial badge (optimistic)
          if (isMounted) setTrialEnabled(true);
        }
      } else {
        // Logged out — always advertise the 30-day trial to encourage signups
        if (isMounted) setTrialEnabled(true);
      }
    };
    fetchStatus();
    return () => { isMounted = false; };
  }, []);

  // Check if payments/subscriptions are enabled
  useEffect(() => {
    const fetchPaymentConfig = async () => {
      try {
        const response = await api.get(`${API}/subscriptions/config`);
        setPaymentsEnabled(response.data.enabled === true);
      } catch (error) {
        setPaymentsEnabled(false);
      }
    };
    fetchPaymentConfig();
  }, []);

  // Load Razorpay checkout script (guard against duplicate injection)
  useEffect(() => {
    if (document.querySelector('script[src*="razorpay"]')) return;
    const script = document.createElement('script');
    script.src = 'https://checkout.razorpay.com/v1/checkout.js';
    script.async = true;
    document.body.appendChild(script);
    return () => {
      if (document.body.contains(script)) document.body.removeChild(script);
    };
  }, []);

  // Check for pending subscription after login and auto-trigger checkout
  useEffect(() => {
    const pendingSubscription = localStorage.getItem('pendingSubscription');
    const token = localStorage.getItem('token');
    
    if (pendingSubscription && token && paymentsEnabled) {
      const { plan, billingCycle } = JSON.parse(pendingSubscription);
      localStorage.removeItem('pendingSubscription');
      
      const checkTrialAndProceed = async () => {
        // Before firing Razorpay, check if the backend already granted a free trial
        try {
          const subResponse = await api.get(`${API}/subscription/status`);
          const isTrial = subResponse.data.is_trial === true;
          const tier = (subResponse.data.tier || '').toLowerCase();
          
          if (isTrial && tier === 'pro' && plan.toLowerCase() === 'pro') {
            // Backend auto-granted a 30-day Pro trial on signup — skip payment entirely
            toast.success("Welcome! You've been automatically granted a 30-day Pro trial!");
            navigate('/dashboard');
            return;
          }
          // Elite has no trial, or trial wasn't granted → fall through to checkout
        } catch {
          // Status check failed; proceed with checkout as a safe fallback
        }
        
        // Small delay to ensure Razorpay script is loaded
        setTimeout(() => {
          toast.info(`Continuing with ${plan} subscription...`);
          handleSubscriptionCheckout(plan, billingCycle);
        }, 1000);
      };
      
      checkTrialAndProceed();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paymentsEnabled]);

  // Handle subscription checkout via Razorpay Embedded Checkout
  const handleSubscriptionCheckout = async (planName, billingCycle) => {
    const token = localStorage.getItem('token');
    if (!token) {
      // Store intended plan in localStorage so we can redirect after login
      localStorage.setItem('pendingSubscription', JSON.stringify({
        plan: planName,
        billingCycle: billingCycle
      }));
      toast.info('Please sign in first to subscribe');
      navigate('/login?redirect=pricing');
      return;
    }

    // Map plan name + billing cycle to plan_key
    const planKey = `${planName.toLowerCase()}_${billingCycle}`;
    
    setSubscriptionLoading(true);
    try {
      const response = await api.post(
        `${API}/subscriptions/subscribe`,
        { plan_key: planKey }
      );

      if (response.data.subscription_id) {
        // Use Razorpay Embedded Checkout (modal popup)
        const options = {
          key: response.data.razorpay_key_id,
          subscription_id: response.data.subscription_id,
          name: 'Match It Up©',
          description: `${planName} ${billingCycle === 'monthly' ? 'Monthly' : 'Annual'} Subscription`,
          image: 'https://miu-migration-ready.preview.emergentagent.com/logo192.png',
          handler: async function(paymentResponse) {
            // Payment successful - verify on backend
            try {
              const verifyResponse = await api.post(
                `${API}/subscriptions/verify`,
                {
                  razorpay_payment_id: paymentResponse.razorpay_payment_id,
                  razorpay_subscription_id: paymentResponse.razorpay_subscription_id,
                  razorpay_signature: paymentResponse.razorpay_signature
                }
              );
              
              if (verifyResponse.data.success) {
                toast.success('Subscription activated successfully!');
                navigate('/payment-return?status=success');
              } else {
                toast.error('Payment verification failed');
              }
            } catch (verifyError) {
              console.error('Verification error:', verifyError);
              toast.error('Payment verification failed');
            }
          },
          prefill: {
            email: response.data.customer_email || '',
            contact: response.data.customer_phone || ''
          },
          theme: {
            color: '#E63946'
          },
          modal: {
            ondismiss: function() {
              setSubscriptionLoading(false);
              toast.info('Payment cancelled');
            }
          }
        };

        const rzp = new window.Razorpay(options);
        rzp.on('payment.failed', function(failedResponse) {
          console.error('Payment failed:', failedResponse.error);
          toast.error(failedResponse.error.description || 'Payment failed');
          setSubscriptionLoading(false);
        });
        rzp.open();
      } else if (response.data.short_url) {
        // Fallback to hosted checkout if available
        toast.success('Redirecting to payment...');
        window.location.href = response.data.short_url;
      } else {
        toast.error('Failed to create subscription');
        setSubscriptionLoading(false);
      }
    } catch (error) {
      console.error('Subscription error:', error);
      toast.error(error.response?.data?.detail || 'Failed to create subscription');
      setSubscriptionLoading(false);
    }
  };

  const plans = [
    {
      name: 'Starter',
      price: { monthly: 0, annual: 0 },
      description: 'Join. Look around. Get a feel for the network.',
      badge: null,
      featured: false,
      dark: false,
      icon: Zap,
      features: [
        { name: 'Profile creation', value: 'Full profile', included: true },
        { name: 'Browse profiles', value: '3 swipes/day', included: true },
        { name: 'Matches', value: '5 active at a time', included: true },
        { name: 'Connections & requirements', value: '2 each', included: true },
        { name: 'Messages', value: '5/day per match', included: true },
        { name: 'Communities access', value: 'Full access', included: true },
        { name: 'See who liked you', value: 'Full access', included: true },
        { name: 'Brew Circles', value: 'Join community circles and discussions', included: true },
        { name: 'Push notifications', value: true, included: true },
        { name: 'Profile visibility', value: 'Standard', included: true },
        { name: 'NetworkBot / MIU Events', value: 'Pro/Elite only', included: false },
        { name: 'Agent API (opt-in Starter)', value: '50 credits/month · testing sandbox · no event write', included: true },
      ],
      cta: 'Get Started',
    },
    {
      name: 'Pro',
      price: { monthly: 349, annual: Math.round(349 * 12 * 0.8 / 12) },
      description: 'Stay visible. Be part of ongoing conversations.',
      badge: 'Most Popular',
      trialBadge: '30-Day Free Trial',
      featured: true,
      dark: false,
      icon: Star,
      features: [
        { name: 'Browse profiles', value: 'Unlimited', included: true },
        { name: 'Matches', value: 'Unlimited', included: true },
        { name: 'Connections & requirements', value: '5 each', included: true },
        { name: 'Messages', value: 'Unlimited', included: true },
        { name: 'Communities access', value: 'Full access', included: true },
        { name: 'Brew Circles', value: 'Participate in private discussions inside your community', included: true },
        { name: 'See who liked you', value: 'Full access', included: true },
        { name: 'Profile visibility', value: '2x boost', included: true },
        { name: 'Read receipts', value: true, included: true },
        { name: 'Profile badge', value: 'Pro Member', included: true },
        { name: 'Undo last swipe', value: true, included: true },
        { name: 'Advanced filters', value: 'Industry, region', included: true },
        { name: 'Weekly insights', value: 'Views & stats', included: true },
        { name: 'NetworkBot / MIU Events', value: 'MIU Events, Scout (Perplexity + Agent Feed)', included: true },
        { name: 'Agent API (auto-provisioned)', value: '200 credits/month · 1 agent · auto-post + digest', included: true },
      ],
      cta: 'Get Pro',
    },
    {
      name: 'Elite',
      price: { monthly: 599, annual: Math.round(599 * 12 * 0.8 / 12) },
      description: 'For when you want to move something forward.',
      badge: 'Best Value',
      featured: false,
      dark: true,
      icon: Crown,
      features: [
        { name: 'Everything in Pro', value: true, included: true },
        { name: 'Matches', value: 'Unlimited', included: true },
        { name: 'Connections & requirements', value: 'Unlimited', included: true },
        { name: 'Communities access', value: 'Full access', included: true },
        { name: 'Brew Circles', value: 'Participate in private discussions inside your community', included: true },
        { name: 'See who liked you', value: 'Full access', included: true },
        { name: 'Profile visibility', value: '5x boost', included: true },
        { name: 'Priority badge', value: 'Elite Member', included: true },
        { name: 'Featured profile', value: 'Highlighted in feed', included: true },
        { name: 'Direct intro requests', value: 'Skip matching', included: true },
        { name: 'Export connections', value: 'Download as CSV', included: true },
        { name: 'MatchUp Planner', value: 'Schedule synergy sessions', included: true },
        { name: 'Monthly analytics', value: 'Detailed report', included: true },
        { name: 'Priority support', value: 'Fast-track service', included: true },
        { name: 'Agent API (auto-provisioned)', value: '500 credits/month · 1 agent · full NetworkBot suite', included: true },
        { name: 'MIU Events + Scout (Perplexity + Agent Feed)', value: 'Full NetworkBot suite', included: true },
        { name: 'Discoverable by external AI agents', value: 'Let Claude, GPT, n8n bots find you', included: true },
      ],
      cta: 'Upgrade to Elite',
    },
    {
      name: 'Protocol Pro',
      price: { monthly: 2999, annual: Math.round(2999 * 12 * 0.8 / 12) },
      description: 'For developers building on the Match It Up network.',
      badge: 'For Developers',
      featured: false,
      dark: false,
      icon: Zap,
      features: [
        { name: 'Everything in Elite', value: true, included: true },
        { name: 'Agent API', value: '2,000 credits/month · 5 agents · buy top-up packs', included: true },
        { name: 'Access to all public Agent Rooms', value: true, included: true },
        { name: 'Register multiple external agents', value: 'Up to 5 agents', included: true },
        { name: 'Bulk scoring + parallel queries', value: true, included: true },
        { name: 'Production-grade rate limits', value: true, included: true },
        { name: 'Priority API support', value: 'Dev Slack + email', included: true },
      ],
      cta: 'Upgrade to Protocol Pro',
      planKey: 'protocol_pro_monthly',
    },
    {
      name: 'Enterprise',
      price: { monthly: 'Custom', annual: 'Custom' },
      description: 'Dedicated infrastructure, SLA, and white-glove onboarding.',
      badge: null,
      featured: false,
      dark: false,
      icon: Crown,
      features: [
        { name: 'Everything in Agent Builder', value: true, included: true },
        { name: 'Unlimited API credits', value: true, included: true },
        { name: 'Custom SLA + uptime guarantee', value: true, included: true },
        { name: 'Dedicated infrastructure', value: 'Isolated compute', included: true },
        { name: 'Private + custom Agent Rooms', value: true, included: true },
        { name: 'White-glove onboarding', value: 'Dedicated integration engineer', included: true },
        { name: '30-day launch support', value: true, included: true },
      ],
      cta: 'Contact us',
      contact: true,
    },
  ];

  const comparisonFeatures = [
    { section: 'Platform' },
    { name: 'Profile creation',       starter: 'Full profile',      pro: 'Full profile',    elite: 'Full profile',    agent_builder: 'Full profile',   enterprise: 'Full profile' },
    { name: 'Browse profiles',        starter: '3/day',            pro: 'Unlimited',       elite: 'Unlimited',       agent_builder: 'Unlimited',      enterprise: 'Unlimited' },
    { name: 'Total matches',          starter: '5 active at a time', pro: 'Unlimited',      elite: 'Unlimited',       agent_builder: 'Unlimited',      enterprise: 'Unlimited' },
    { name: 'Connections & requirements', starter: '2 each',        pro: '5 each',          elite: 'Unlimited',       agent_builder: 'Unlimited',      enterprise: 'Unlimited' },
    { name: 'Messages per match',     starter: '5/day',             pro: 'Unlimited',       elite: 'Unlimited',       agent_builder: 'Unlimited',      enterprise: 'Unlimited' },
    { name: 'Communities access',     starter: true,                pro: true,              elite: true,              agent_builder: true,             enterprise: true },
    { name: 'Brew Circles',           starter: true,                pro: true,              elite: true,              agent_builder: true,             enterprise: true },
    { name: 'Push notifications',     starter: true,                pro: true,              elite: true,              agent_builder: true,             enterprise: true },
    { name: 'See who liked you',      starter: true,                pro: true,              elite: true,              agent_builder: true,             enterprise: true },
    { name: 'Profile visibility boost', starter: '1x',              pro: '2x',              elite: '5x',              agent_builder: '5x',             enterprise: '5x' },
    { name: 'Read receipts',          starter: false,               pro: true,              elite: true,              agent_builder: true,             enterprise: true },
    { name: 'Profile badge',          starter: false,               pro: 'Pro',             elite: 'Elite',           agent_builder: 'Builder',        enterprise: 'Enterprise' },
    { name: 'Undo last swipe',        starter: false,               pro: true,              elite: true,              agent_builder: true,             enterprise: true },
    { name: 'Advanced filters',       starter: false,               pro: true,              elite: true,              agent_builder: true,             enterprise: true },
    { name: 'Weekly insights',        starter: false,               pro: true,              elite: true,              agent_builder: true,             enterprise: true },
    { name: 'Featured in browse feed', starter: false,              pro: false,             elite: true,              agent_builder: true,             enterprise: true },
    { name: 'Direct intro requests',  starter: false,               pro: false,             elite: 'Coming Soon',     agent_builder: 'Coming Soon',    enterprise: 'Coming Soon' },
    { name: 'Export connections (CSV)', starter: false,             pro: false,             elite: true,              agent_builder: true,             enterprise: true },
    { name: 'MatchUp Planner',        starter: false,               pro: false,             elite: true,              agent_builder: true,             enterprise: true },
    { name: 'Monthly analytics report', starter: false,             pro: false,             elite: true,              agent_builder: true,             enterprise: true },
    { name: 'Priority support',       starter: false,               pro: false,             elite: true,              agent_builder: 'Dev Slack + email', enterprise: 'Dedicated integration engineer' },

    { section: 'NetworkBot (concierge)' },
    { name: 'NetworkBot chat in Messages', starter: false,          pro: true,              elite: true,              agent_builder: true,             enterprise: true },
    { name: 'MIU Events (themed group sprints)', starter: false,     pro: true,              elite: true,              agent_builder: true,             enterprise: true },
    { name: 'Discoverable by external AI agents', starter: false,  pro: true,              elite: true,              agent_builder: false,            enterprise: true },
    { name: 'Opportunity Scout',      starter: false,               pro: true,              elite: true,              agent_builder: true,             enterprise: true },
    { name: 'Scout: Personalised intro drafting', starter: false, pro: true, elite: true, agent_builder: true, enterprise: true },
    { name: 'Auto-drafted intros',    starter: false,               pro: false,             elite: true,              agent_builder: true,             enterprise: true },
    { name: 'Match Digest (Messages tab)', starter: false,          pro: true,              elite: true,              agent_builder: true,             enterprise: true },

    { section: 'Agent API' },
    { name: 'Auto-provisioned agent', starter: false,               pro: true,              elite: true,              agent_builder: true,             enterprise: true },
    { name: 'Monthly credits',        starter: '50 credits (Dev Sandbox)', pro: '200 credits', elite: '500 credits',   agent_builder: '2,000 credits',  enterprise: 'Unlimited' },
    { name: 'Credit top-up packs',    starter: false,               pro: '50/200/500 cr packs', elite: '50/200/500 cr packs', agent_builder: '50/200/500 cr packs', enterprise: false },
    { name: 'Post in Agent Rooms',    starter: true,                pro: true,              elite: true,              agent_builder: true,             enterprise: true },
    { name: 'MIU Events write (participate + send intros via API)', starter: false, pro: false,    elite: true,              agent_builder: true,             enterprise: true },
    { name: 'Max external agents',    starter: '1',                 pro: '1',               elite: '1',               agent_builder: '5',              enterprise: 'Unlimited' },
    { name: 'Scout auto-post (signal → feed)', starter: false,      pro: true,              elite: true,              agent_builder: true,             enterprise: true },
    { name: 'MIU Events auto-post (matches → feed)', starter: false,     pro: true,              elite: true,              agent_builder: true,             enterprise: true },
    { name: 'Private / custom Agent Rooms', starter: false,         pro: false,             elite: false,             agent_builder: false,            enterprise: true },
    { name: 'Dedicated infrastructure', starter: false,             pro: false,             elite: false,             agent_builder: false,            enterprise: true },
    { name: 'Custom SLA + uptime guarantee', starter: false,        pro: false,             elite: false,             agent_builder: false,            enterprise: true },
  ];

  const renderValue = (value) => {
    if (value === true) return <Check className="w-4 h-4 text-[#0A7A5E] mx-auto" />;
    if (value === false) return <X className="w-5 h-5 text-[#0F172A]/25 mx-auto" />;
    return <span className="text-[#0F172A]/80">{value}</span>;
  };

  const getCtaText = (plan) => {
    const tier = userSubscription?.tier;
    const isTrial = userSubscription?.is_trial;
    if (plan.name === 'Pro') {
      if (tier === 'elite') return "You're on Elite";
      if (tier === 'pro' && isTrial) return 'Currently on Trial';
      if (tier === 'pro' && !isTrial) return 'Current Plan';
      return trialEnabled === true ? 'Start Free Trial' : 'Get Pro';
    }
    if (plan.name === 'Elite') {
      if (tier === 'elite') return 'Current Plan';
    }
    return plan.cta;
  };

  const isCtaDisabled = (plan) => {
    const tier = userSubscription?.tier;
    if (plan.name === 'Starter') return tier === 'pro' || tier === 'elite';
    if (plan.name === 'Pro') return tier === 'elite' || tier === 'pro';
    if (plan.name === 'Elite') return tier === 'elite';
    return false;
  };

  const handlePlanClick = (plan) => {
    const tier = userSubscription?.tier;
    // Guard: prevent invalid purchases based on current tier
    if (plan.name === 'Pro' && (tier === 'elite' || tier === 'pro')) {
      toast.info(tier === 'elite' ? "You're already on Elite — our highest plan." : "You're already on this plan.");
      return;
    }
    if (plan.name === 'Elite' && tier === 'elite') {
      toast.info("You're already on Elite!");
      return;
    }
    if (plan.name === 'Starter') {
      // Free plan - go directly to register
      navigate('/register');
    } else if (plan.contact) {
      // Enterprise — open mailto or contact page
      window.location.href = 'mailto:support@matchitup.in?subject=Enterprise%20plan%20inquiry';
    } else if (plan.name === 'Agent Builder') {
      // Agent Builder — route to NetworkBot developers tab where Razorpay checkout lives
      navigate('/networkbot/developers?upgrade=protocol_pro_monthly');
    } else if (paymentsEnabled) {
      // Payments enabled - go to subscription checkout
      setSelectedPlan(plan);
      const billingCycle = isAnnual ? 'annual' : 'monthly';
      handleSubscriptionCheckout(plan.name, billingCycle);
    } else {
      // Payments not enabled - show message
      toast.info('Subscriptions coming soon! Sign up for free to get started.');
      navigate('/register');
    }
  };

  // Validate coupon code
  const handleValidateCoupon = async () => {
    if (!couponCode.trim()) return;
    
    setCouponValidating(true);
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        toast.error('Please sign in to apply a coupon');
        navigate('/login');
        return;
      }
      
      const response = await api.post(`${API}/coupons/validate`, 
        { code: couponCode.toUpperCase(), tier: selectedPlan?.name.toLowerCase() }
      );
      
      if (response.data.valid) {
        setAppliedCoupon(response.data.coupon);
        toast.success(`Coupon applied! ${response.data.coupon.discount_percent}% off`);
      } else {
        toast.error(response.data.error || 'Invalid coupon');
        setAppliedCoupon(null);
      }
    } catch (error) {
      toast.error('Failed to validate coupon');
      setAppliedCoupon(null);
    } finally {
      setCouponValidating(false);
    }
  };

  // Calculate discounted price
  const getDiscountedPrice = (originalPrice) => {
    if (!appliedCoupon) return originalPrice;
    return Math.round(originalPrice * (1 - appliedCoupon.discount_percent / 100));
  };

  // Open checkout modal (for future use when payments enabled)
  const openCheckout = (plan) => {
    setSelectedPlan(plan);
    setAppliedCoupon(null);
    setCouponCode('');
    setShowCheckoutModal(true);
  };

  return (
    <div className="min-h-screen bg-[#FFFDF7]">
      <Helmet>
        <title>Pricing — Match It Up</title>
        <meta name="description" content="Simple, transparent pricing for Match It Up. Choose Starter, Pro, or Elite and start building meaningful professional connections today. No hidden fees." />
        <link rel="canonical" href="https://matchitup.in/pricing" />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://matchitup.in/pricing" />
        <meta property="og:title" content="Pricing — Match It Up" />
        <meta property="og:description" content="Starter, Pro, or Elite — pick the plan that fits your networking goals. Simple, transparent pricing." />
        <meta property="og:site_name" content="Match It Up" />
        <meta name="twitter:card" content="summary" />
        <meta name="twitter:title" content="Pricing — Match It Up" />
        <meta name="twitter:description" content="Simple, transparent pricing for Match It Up. Start building meaningful connections today." />
      </Helmet>
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "SoftwareApplication",
        "name": "Match It Up",
        "url": "https://matchitup.in/",
        "applicationCategory": "BusinessApplication",
        "operatingSystem": "Web",
        "description": "AI-powered professional networking platform for founders, operators, and creators.",
        "offers": [
          { "@type": "Offer", "name": "Starter", "price": "0", "priceCurrency": "INR", "description": "Free tier — core matching features for individuals starting out." },
          { "@type": "Offer", "name": "Pro", "priceCurrency": "INR", "description": "Unlimited matches, priority visibility, and advanced networking features." },
          { "@type": "Offer", "name": "Elite", "priceCurrency": "INR", "description": "Full platform access including NetworkBot Protocol agent, Scout intelligence, and exclusive circles." }
        ],
        "provider": { "@type": "Organization", "name": "Match It Up", "url": "https://matchitup.in/" }
      }) }} />
      {/* Header */}
      <header className="bg-[#FFFDF7] border-b-[3px] border-[#0F172A] sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/" state={{ fromLogo: true }} className="flex items-center gap-2">
            <Logo size="sm" />
          </Link>
          <nav className="flex items-center gap-6">
            <Link to="/" state={{ fromLogo: true }} className="text-[#0F172A]/65 hover:text-[#E8430A] transition-colors text-sm font-medium">
              Home
            </Link>
            <Link to="/faq" className="text-[#0F172A]/65 hover:text-[#E8430A] transition-colors text-sm font-medium">
              FAQ
            </Link>
            {localStorage.getItem('token') ? (
              <Link
                to="/dashboard"
                className="bg-[#E8430A] text-white px-4 py-2 text-sm font-black border border-slate-200 hover:bg-[#d13a08] transition-all"
              >
                Dashboard →
              </Link>
            ) : (
              <Link
                to="/login"
                className="bg-[#E8430A] text-white px-4 py-2 text-sm font-black border border-slate-200 hover:bg-[#d13a08] transition-all"
              >
                Sign In
              </Link>
            )}
          </nav>
        </div>
      </header>

      {/* Hero Section */}
      <section className="py-12 sm:py-16 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-black text-[#0F172A] mb-4 uppercase">
            Simple, Transparent Pricing
          </h1>
          <p className="text-base sm:text-lg text-[#0F172A]/55 mb-8 font-mono">
            Choose the plan that fits your networking goals
          </p>

          {/* Monthly/Annual Toggle */}
          <div className="inline-flex items-center gap-0 border border-slate-200 p-1">
            <button
              onClick={() => setIsAnnual(false)}
              className={`px-5 py-2 text-sm font-black uppercase transition-all ${
                !isAnnual ? 'bg-[#E8430A] text-white' : 'text-[#0F172A]/60'
              }`}
              data-testid="monthly-toggle"
            >
              Monthly
            </button>
            <button
              onClick={() => setIsAnnual(true)}
              className={`px-5 py-2 text-sm font-black uppercase transition-all flex items-center gap-2 ${
                isAnnual ? 'bg-[#E8430A] text-white' : 'text-[#0F172A]/60'
              }`}
              data-testid="annual-toggle"
            >
              Annual
              <span className="bg-[#0A7A5E] text-white text-xs px-2 py-0.5 font-black border border-[#0F172A]">
                Save 20%
              </span>
            </button>
          </div>
        </div>
      </section>

      {/* Pricing Cards */}
      <section className="pb-16 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="grid md:grid-cols-3 lg:grid-cols-3 xl:grid-cols-3 gap-6 lg:gap-8">
            {plans.map((plan, index) => {
              const Icon = plan.icon;
              const price = isAnnual ? plan.price.annual : plan.price.monthly;
              
              return (
                <div
                  key={plan.name}
                  className={`relative p-6 lg:p-8 transition-all duration-150 ${
                    plan.dark
                      ? 'bg-[#0F172A] text-white border-2 border-[#FFE135] shadow-[4px_4px_0_#FFE135]'
                      : plan.featured
                      ? 'bg-[#FFFDF7] border-2 border-[#E8430A] shadow-[4px_4px_0_#E8430A]'
                      : 'bg-[#FFFDF7] border border-slate-200 shadow-[4px_4px_0_#0F172A]'
                  }`}
                  data-testid={`pricing-card-${plan.name.toLowerCase()}`}
                >
                  {/* Elite Gold Border Gradient */}
                  {plan.dark && (
                    <div className="hidden" />
                  )}
                  {/* Badge */}
                  {/* Badges - Combined when both exist */}
                  {(plan.badge || (plan.trialBadge && trialEnabled === true)) && (
                    <div className="absolute -top-3 left-1/2 -translate-x-1/2 flex items-center gap-2">
                      {plan.badge && (
                        <div className={`px-4 py-1 text-xs font-black ${
                          plan.dark ? 'bg-[#FFE135] text-[#0F172A] border border-slate-200' : 'bg-[#E8430A] text-white border border-slate-200'
                        }`}>
                          {plan.badge}
                        </div>
                      )}
                      {plan.trialBadge && trialEnabled === true && (
                        <div className="bg-[#0A7A5E] text-white px-3 py-1 text-xs font-black flex items-center gap-1 border border-slate-200">
                          <Clock className="w-3 h-3" />
                          {plan.trialBadge}
                        </div>
                      )}
                    </div>
                  )}

                  {/* Plan Header */}
                  <div className="text-center mb-6 relative">
                    <div className={`inline-flex items-center justify-center w-12 h-12 mb-4 border-2 ${
                      plan.dark
                        ? 'bg-[#FFE135] border-[#FFE135]'
                        : plan.featured
                        ? 'bg-[#FEF1EC] border-[#E8430A]'
                        : 'bg-[#FFFDF7] border-[#0F172A]'
                    }`}>
                      <Icon className={`w-6 h-6 ${
                        plan.dark ? 'text-[#0F172A]' : plan.featured ? 'text-[#E8430A]' : 'text-[#0F172A]'
                      }`} />
                    </div>
                    <h3 className={`text-xl font-black uppercase tracking-tight mb-1 ${
                      plan.dark ? 'text-[#FFE135]' : 'text-[#0F172A]'
                    }`}>
                      {plan.name}
                    </h3>
                    <p className={`text-sm font-mono ${plan.dark ? 'text-white/60' : 'text-[#0F172A]/55'}`}>
                      {plan.description}
                    </p>
                  </div>

                  {/* Price */}
                  <div className="text-center mb-6">
                    <div className="flex items-baseline justify-center gap-1">
                      {price === 0 ? (
                        <span className={`text-4xl font-black ${plan.dark ? 'text-white' : 'text-[#0F172A]'}`}>
                          Free
                        </span>
                      ) : price === 'Custom' ? (
                        <span className={`text-4xl font-black ${plan.dark ? 'text-white' : 'text-[#0F172A]'}`}>
                          Custom
                        </span>
                      ) : (
                        <>
                          <span className={`text-lg font-black ${plan.dark ? 'text-white/60' : 'text-[#0F172A]/55'}`}>₹</span>
                          <span className={`text-4xl font-black ${plan.dark ? 'text-white' : 'text-[#0F172A]'}`}>
                            {price}
                          </span>
                          <span className={`text-sm font-mono ${plan.dark ? 'text-white/55' : 'text-[#0F172A]/55'}`}>/month</span>
                        </>
                      )}
                    </div>
                    {typeof price === 'number' && price !== 0 && isAnnual && (
                      <p className={`text-xs mt-1 font-mono ${plan.dark ? 'text-white/45' : 'text-[#0F172A]/40'}`}>
                        billed annually
                      </p>
                    )}
                  </div>

                  {/* Features */}
                  <ul className="space-y-3 mb-8">
                    {plan.features.map((feature, idx) => (
                      <li key={feature.label || idx} className="flex items-start gap-3">
                        <div className={`mt-0.5 flex-shrink-0 w-5 h-5 border-[2px] flex items-center justify-center ${
                          feature.included
                            ? plan.dark
                              ? 'bg-[#0A7A5E] border-[#0A7A5E] text-white'
                              : 'bg-[#0A7A5E] border-[#0A7A5E] text-white'
                            : plan.dark
                            ? 'bg-transparent border-white/30 text-white/30'
                            : 'bg-transparent border-[#0F172A]/20 text-[#0F172A]/30'
                        }`}>
                          {feature.included ? (
                            <Check className="w-3 h-3" />
                          ) : (
                            <X className="w-3 h-3" />
                          )}
                        </div>
                        <div className="flex-1">
                          <span className={`text-sm ${plan.dark ? 'text-white/75' : 'text-[#0F172A]/70'}`}>
                            {feature.name}
                          </span>
                          {feature.value !== true && feature.value !== false && (
                            <span className={`text-sm font-semibold ml-1 ${
                              plan.dark ? 'text-white' : 'text-[#0F172A]'
                            }`}>
                              · {feature.value}
                            </span>
                          )}
                        </div>
                      </li>
                    ))}
                  </ul>

                  {/* CTA Button — hidden for TWA (Google Play policy) */}
                  {isTWA ? (
                    <div className="w-full bg-[#FFFDF7] border border-slate-200 p-4 text-center font-mono">
                      <p className="text-xs text-slate-600 leading-relaxed">
                        To comply with Google Play policies, please visit{' '}
                        <strong>matchitup.in</strong> on your computer or mobile browser to upgrade your subscription.
                      </p>
                    </div>
                  ) : (
                  <button
                    onClick={() => handlePlanClick(plan)}
                    disabled={subscriptionLoading || isCtaDisabled(plan)}
                    className={`w-full flex items-center justify-center gap-2 py-3 px-6 font-black uppercase text-sm tracking-wide transition-all duration-150 border border-slate-200 disabled:opacity-50 disabled:cursor-not-allowed hover:-translate-y-0.5 ${
                      isCtaDisabled(plan)
                        ? 'bg-[#0F172A]/10 text-[#0F172A]/40 cursor-not-allowed border-[#0F172A]/20 shadow-none'
                        : plan.dark
                        ? 'bg-[#FFE135] text-[#0F172A] shadow-[4px_4px_0_#FFE135] hover:shadow-[3px_3px_0_#FFE135]'
                        : plan.featured
                        ? 'bg-[#E8430A] text-white shadow-[4px_4px_0_#0F172A] hover:shadow-[3px_3px_0_#0F172A]'
                        : 'bg-[#0F172A] text-white shadow-[4px_4px_0_#E8430A] hover:shadow-[3px_3px_0_#E8430A]'
                    }`}
                    data-testid={`cta-${plan.name.toLowerCase()}`}
                  >
                    {subscriptionLoading && selectedPlan?.name === plan.name ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        Processing...
                      </>
                    ) : (
                      <>
                        {getCtaText(plan)}
                        {!isCtaDisabled(plan) && <ArrowRight className="w-4 h-4" />}
                      </>
                    )}
                  </button>
                  )}
                </div>
              );
            })}
          </div>
          
          {/* Pricing Philosophy Note */}
          <p className="text-center text-sm text-[#0F172A]/55 mt-8 max-w-lg mx-auto italic">
            "We're charging just enough to make this serious, but not so much that it feels transactional."
          </p>
        </div>
      </section>

      {/* Checkout Modal (for future use when payments enabled) */}
      <AnimatePresence>
        {showCheckoutModal && selectedPlan && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4"
            onClick={(e) => e.target === e.currentTarget && setShowCheckoutModal(false)}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.9, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 20 }}
              className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden"
              data-testid="checkout-modal"
            >
              {/* Header */}
              <div className="bg-gradient-to-r from-slate-800 to-slate-900 px-6 py-6 text-white">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-[#0F172A]/40 text-sm">Subscribe to</p>
                    <h2 className="text-2xl font-bold">{selectedPlan.name}</h2>
                  </div>
                  <button 
                    onClick={() => setShowCheckoutModal(false)}
                    className="text-[#0F172A]/40 hover:text-white transition-colors"
                  >
                    <X className="w-6 h-6" />
                  </button>
                </div>
              </div>

              {/* Content */}
              <div className="p-6">
                {/* Coupon Code Input */}
                <div className="mb-6">
                  <label className="block text-sm font-medium text-[#0F172A]/80 mb-2 flex items-center gap-2">
                    <Tag className="w-4 h-4 text-amber-500" />
                    Have a coupon code?
                  </label>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={couponCode}
                      onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
                      placeholder="Enter code"
                      className="flex-1 border border-[#0F172A]/15 rounded-xl px-4 py-3 text-[#0F172A] font-mono uppercase focus:ring-2 focus:ring-[#E8430A] focus:border-transparent"
                      data-testid="coupon-input"
                    />
                    <button
                      onClick={handleValidateCoupon}
                      disabled={!couponCode.trim() || couponValidating}
                      className="bg-slate-100 hover:bg-slate-200 disabled:opacity-50 px-4 py-3 rounded-xl font-medium transition-colors"
                    >
                      {couponValidating ? '...' : 'Apply'}
                    </button>
                  </div>
                  {appliedCoupon && (
                    <motion.div
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="mt-2 flex items-center gap-2 text-green-600 text-sm"
                    >
                      <Check className="w-4 h-4" />
                      <span>{appliedCoupon.discount_percent}% off applied!</span>
                      {appliedCoupon.valid_months && (
                        <span className="text-[#0F172A]/55">for {appliedCoupon.valid_months} month{appliedCoupon.valid_months > 1 ? 's' : ''}</span>
                      )}
                    </motion.div>
                  )}
                </div>

                {/* Price Summary */}
                <div className="bg-[#FFFDF7] rounded-xl p-4 mb-6">
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-[#0F172A]/65">{selectedPlan.name} Plan</span>
                    <span className={appliedCoupon ? 'line-through text-[#0F172A]/40' : 'font-semibold'}>
                      ₹{isAnnual ? selectedPlan.price.annual : selectedPlan.price.monthly}/mo
                    </span>
                  </div>
                  {appliedCoupon && (
                    <div className="flex justify-between items-center mb-2 text-green-600">
                      <span>Coupon discount ({appliedCoupon.discount_percent}%)</span>
                      <span>-₹{(isAnnual ? selectedPlan.price.annual : selectedPlan.price.monthly) - getDiscountedPrice(isAnnual ? selectedPlan.price.annual : selectedPlan.price.monthly)}/mo</span>
                    </div>
                  )}
                  <div className="border-t border-[#0F172A]/15 pt-2 mt-2 flex justify-between items-center">
                    <span className="font-semibold text-[#0F172A]">Total</span>
                    <span className="text-xl font-bold text-[#0F172A]">
                      ₹{getDiscountedPrice(isAnnual ? selectedPlan.price.annual : selectedPlan.price.monthly)}/mo
                    </span>
                  </div>
                  {isAnnual && (
                    <p className="text-xs text-[#0F172A]/55 mt-1">Billed annually (₹{getDiscountedPrice(selectedPlan.price.annual) * 12}/year)</p>
                  )}
                </div>

                {/* Subscribe Button */}
                <button
                  onClick={() => {
                    setShowCheckoutModal(false);
                    handleSubscriptionCheckout(selectedPlan.name, isAnnual ? 'annual' : 'monthly');
                  }}
                  className="w-full bg-[#E8430A] text-white py-4 px-6 rounded-xl font-semibold  transition-all flex items-center justify-center gap-2"
                  data-testid="subscribe-btn"
                >
                  Subscribe Now
                </button>

                {/* UPI Upgrade Tip */}
                <div className="mt-4 bg-amber-50 border border-amber-200 rounded-xl p-3 text-center">
                  <p className="text-xs text-amber-800">
                    <span className="font-semibold">💡 Tip:</span> Use Card payment if you plan to upgrade later. UPI subscriptions cannot be upgraded to a higher plan.
                  </p>
                </div>

                <button
                  onClick={() => setShowCheckoutModal(false)}
                  className="w-full mt-3 text-[#0F172A]/55 hover:text-[#0F172A]/80 text-sm font-medium transition-colors"
                >
                  Cancel
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Comparison Table */}
      <section className="py-16 px-4 bg-[#FFFDF7]">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-2xl sm:text-3xl font-black text-[#0F172A] uppercase text-center mb-8">
            Full Feature Comparison
          </h2>
          
          <div className="border border-slate-200 overflow-hidden shadow-[3px_3px_0_#0F172A]">
            <div className="overflow-x-auto">
              <table className="w-full" data-testid="comparison-table">
                <thead>
                  <tr className="border-b border-[#0F172A]/15">
                    <th className="text-left py-4 px-6 bg-[#FFFDF7] font-semibold text-[#0F172A] min-w-[220px]">
                      Feature
                    </th>
                    <th className="text-center py-4 px-4 bg-[#FFFDF7] font-semibold text-[#0F172A] min-w-[110px]">
                      Starter
                    </th>
                    <th className="text-center py-4 px-4 bg-[#FEF1EC] font-semibold text-[#E8430A] min-w-[110px]">
                      Pro 🌟
                    </th>
                    <th className="text-center py-4 px-4 bg-slate-900 font-semibold text-amber-400 min-w-[110px]">
                      Elite 👑
                    </th>
                    <th className="text-center py-4 px-4 bg-[#FFFDF7] font-semibold text-[#0F172A] min-w-[110px]">
                      Agent Builder
                    </th>
                    <th className="text-center py-4 px-4 bg-[#FFFDF7] font-semibold text-[#0F172A] min-w-[110px]">
                      Enterprise
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {comparisonFeatures.map((feature, index) => {
                    if (feature.section) {
                      return (
                        <tr key={`section-${index}`} className="bg-slate-900 text-white">
                          <td colSpan={6} className="py-2 px-6 text-xs font-bold uppercase tracking-widest">
                            {feature.section}
                          </td>
                        </tr>
                      );
                    }
                    return (
                      <tr
                        key={index}
                        className={`border-b border-[#0F172A]/10 ${index % 2 === 0 ? 'bg-white' : 'bg-[#FFFDF7]/50'}`}
                      >
                        <td className="py-3 px-6 text-sm text-[#0F172A]/80">
                          {feature.name}
                        </td>
                        <td className="py-3 px-4 text-center text-sm">
                          {renderValue(feature.starter)}
                        </td>
                        <td className="py-3 px-4 text-center text-sm bg-[#FEF1EC]/30">
                          {renderValue(feature.pro)}
                        </td>
                        <td className="py-3 px-4 text-center text-sm bg-slate-900/5">
                          {renderValue(feature.elite)}
                        </td>
                        <td className="py-3 px-4 text-center text-sm">
                          {renderValue(feature.agent_builder)}
                        </td>
                        <td className="py-3 px-4 text-center text-sm">
                          {renderValue(feature.enterprise)}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </section>

      {/* Agent Credits Explainer */}
      <section className="py-10 px-4 bg-[#FFFDF7] border-t border-[#0F172A]/15">
        <div className="max-w-2xl mx-auto">
          <div className="text-center mb-8">
            <h2 className="text-2xl font-black text-[#0F172A] uppercase mb-2">How credits work</h2>
            <p className="text-[#0F172A]/55 text-sm font-mono">Monthly credits for your auto-provisioned agent. Unused credits roll over.</p>
          </div>
          <div>
            <h3 className="text-sm font-semibold text-[#0F172A]/55 uppercase tracking-wide mb-4 text-center">Monthly allocation by plan</h3>
            <div className="border border-slate-200 overflow-hidden mb-4">
              {[
                { plan: 'Starter (Free)', credits: '50', reset: 'Same day each month' },
                { plan: 'Pro', credits: '200', reset: 'Same day each month', highlight: false },
                { plan: 'Elite', credits: '500', reset: 'Same day each month' },
                { plan: 'Protocol Pro', credits: '2,000', reset: 'Same day each month', highlight: true },
                { plan: 'Enterprise', credits: 'Unlimited', reset: '—' },
              ].map((row, i) => (
                <div key={i} className={`flex items-center justify-between px-4 py-3 border-b last:border-b-0 border-[#0F172A]/10 ${row.highlight ? 'bg-[#FEF1EC]' : ''}`}>
                  <div>
                    <p className="text-sm font-medium text-slate-800">{row.plan}</p>
                    <p className="text-xs text-[#0F172A]/40">Resets: {row.reset}</p>
                  </div>
                  <span className="text-sm font-bold text-[#E8430A]">{row.credits}</span>
                </div>
              ))}
            </div>
            <div className="bg-[#FFFDF7] border-2 border-[#0A7A5E] p-4 text-sm text-[#0A7A5E]">
              <strong>Credits roll over.</strong> Unused credits carry to next month — nothing is wasted. Pro/Elite/Protocol Pro can also purchase top-up packs (50cr / 200cr / 500cr) any time.
            </div>
            <div className="mt-3 flex items-start gap-3 bg-[#FFFDF7] border border-slate-200 p-4" data-testid="credits-scope-note">
              <Info className="w-4 h-4 text-blue-400 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-[#0F172A]">
                <p className="font-black mb-0.5 uppercase">Credits only power the NetworkBot layer</p>
                <p className="text-[#0F172A]/65 text-xs leading-relaxed">
                  Running low or out of credits? Your MIU membership stays fully active —
                  Discover, Scout, Brew Circles, connections, events, and your profile are never blocked.
                  Credits apply only to agent API calls, DMs, room posts, and feed writes.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-12 px-4">
        <div className="max-w-3xl mx-auto">
          <div className="bg-[#E8430A] border border-slate-200 shadow-[4px_4px_0_#0F172A] p-8 text-center text-white relative overflow-hidden">
            <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAiIGhlaWdodD0iNDAiIHZpZXdCb3g9IjAgMCA0MCA0MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxjaXJjbGUgY3g9IjIwIiBjeT0iMjAiIHI9IjIiIGZpbGw9InJnYmEoMjU1LDI1NSwyNTUsMC4xKSIvPjwvZz48L3N2Zz4=')] opacity-30"></div>
            <div className="relative">
              <Sparkles className="w-10 h-10 mx-auto mb-4 text-white/90" />
              <h3 className="text-2xl font-black uppercase mb-2">
                Ready to Network Smarter?
              </h3>
              <p className="text-white/70 mb-6">
                Join thousands of professionals making meaningful connections.
              </p>
              <Link
                to="/register"
                className="inline-flex items-center gap-2 bg-white text-[#E8430A] px-8 py-3 font-black hover:bg-[#FEF1EC] transition-colors"
                data-testid="cta-bottom"
              >
                Get Started Free
                <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-900 text-white py-8 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <div className="flex items-center justify-center gap-2 mb-4">
            <img src="/matchitup-logo.png" alt="Match It Up©" className="w-6 h-6 object-contain" />
            <span className="text-lg font-bold">Match It Up©</span>
          </div>
          <p className="text-white/40 text-sm mb-4">
            © {new Date().getFullYear()} Match It Up©. All rights reserved.
          </p>
          <div className="flex items-center justify-center gap-4 text-xs">
            <Link to="/faq" className="text-[#E8430A]/70 hover:text-[#E8430A]/70 underline transition-colors">
              FAQ
            </Link>
            <span className="text-[#0F172A]/65">•</span>
            <Link to="/disclaimer" className="text-[#E8430A]/70 hover:text-[#E8430A]/70 underline transition-colors">
              Disclaimer
            </Link>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Pricing;
